/**
 * InsuranceAgreementArea — "Is the instrument executed by both parties?"
 *
 * THE PARTIES COME FIRST BECAUSE THEY ARE WHAT THE DOCUMENT CANNOT BE GENERATED WITHOUT. Seven of
 * the merge keys both insurance templates quote are the counterparty's name, address and phone and
 * the Vehicle's model, year of manufacture, colour and registration — two more than the operational
 * advance's Schedule, which prints neither the year nor the colour — and NONE of them is edited on
 * this dossier: they are facts about the contract, fixed on its own driver and vehicle areas. So
 * the block states them and links out, exactly as the advance dossier's parties block does. The last
 * chance anybody has to notice that the wrong person or the wrong car has been named is the moment
 * before the instrument is minted over them.
 *
 * AND THE COUNTERPARTY IS NAMED FOR THE INSTRUMENT [D-B45]. The Advance's is the Borrower, the
 * Payment Record's is the Driver — a Record records a payment rather than a bargain, and that is
 * also the namespace the resolver answers those three keys under. A block that called them the
 * Borrower on a Record would name a role nobody holds on the page it is proof-reading.
 *
 * THE INSURANCE PERIOD IS HERE AS A READ WITH A WAY BACK. Both particulars grids print it, so it
 * belongs on the list of things to check before generating; it is typed on the Policy canvas, which
 * the row links to rather than reproducing the fields.
 *
 * SENDING IS WHAT GENERATES IT, AND NOTHING ON THE BOARD BLOCKS THE SEND. `agreement_create` is
 * called by the row's own button whenever no document exists, and `missing[]` is only fetched once
 * one does — so the pre-flight below the row is ADVISORY and the lede says so. An unresolved key
 * prints literally on a document a driver can be handed and can sign, which is why the rail carries
 * the hire purchase reference as a row of its own.
 *
 * THE D-B55 LOCK IS THE BOARD'S OWN, NOT A SECOND READING OF IT. `advanceLockedFor` hangs
 * `ADVANCE_LOCK_REASON` on this slot while the driver has not signed the hire purchase agreement —
 * a paper `contract_agreements` row satisfying it as fully as a signed seat does — and this line is
 * drawn by comparing against that exported constant, so the padlock on the row and the sentence
 * above it can never describe two different rules.
 *
 * A PAPER INSTRUMENT IS A READ AND PERMANENTLY SO. `agreement_create` refuses every loan whose
 * `insurance_loan_agreements` row is signed with `COALESCE(origin,'SCANNED') = 'SCANNED'`, so the
 * row draws the original and its scan, and no ceremony at all.
 *
 * AND A JUNCTION WITH NO LOAN HAS NO SUBJECT AT ALL. 54 live junctions are that shape: cover
 * recorded, nothing financed, nothing to paper. `insurancePanelEmptyText` is the one author of what
 * that says, along with the three other ways this list comes back empty.
 */

import AgreementSlotList, {
    type ContractAgreementsModel,
} from '@/components/agreements/AgreementSlotList';
import {
    ADVANCE_LOCK_REASON,
    insurancePanelEmptyText,
    type BoardSlot,
} from '@/components/agreements/boardSlots';
import { INSTRUMENT_TITLES, type InsuranceInstrument } from '@/lib/agreements/instrument';

import { Block, Blocks, Hint, LinkLike, LockLine, Rec, Rows } from '../../areas/blocks';
import type { SetupArea } from '../../steps';
import type { InsuranceDriverFacts, InsuranceVehicleFacts } from '../insuranceState';

interface Props {
    /** The route's subject: an existing policy, or the create flow. */
    policyId: number | 'new';
    /** The PAGE's signing model, at `junctionScope: 'all'`. */
    agreements: ContractAgreementsModel;
    /** `insuranceSlotsForLoan(agreements.slots, loanId)` — this policy's row, and only this one. */
    slots: BoardSlot[];
    /** The junction carries an `insurance_loans` row: there is a subject to generate over. */
    hasLoan: boolean;
    /** Which instrument this year would be published on [D-B45]. */
    instrument: InsuranceInstrument;
    driver: InsuranceDriverFacts;
    vehicle: InsuranceVehicleFacts;
    /**
     * `Jan 4, 2026 — Jan 3, 2027`, as both particulars grids print it. Null where it is not set.
     *
     * OFF THE DRAFT AND NOT OFF THE SAVED ROW, which is `policyPeriodText(form.startsAt,
     * form.expiresAt)`: this block is read before a document is generated, and what the document
     * will carry is what the next save writes. On the create flow there is no saved row at all, and
     * a row reading "Not set" beside a period the admin typed on the Cover canvas would be the
     * proof-read contradicting the page it is proof-reading.
     */
    period: string | null;
    /** The number both instruments quote — live document, else [D-B52]'s paper designation. */
    hpReference: string | null;
    /** The driver has signed the hire purchase agreement — the D-B55 gate. */
    hpDriverSigned: boolean;
    /** Where a merge-field pre-flight or a locked slot sends the admin. TOTAL over the vocabulary. */
    onDeepLink: (step: string, field?: string) => void;
    /** Out to the contract's own setup canvas, where the parties are edited. */
    onGoToSetupArea: (area: SetupArea) => void;
    /** Back to this dossier's Policy canvas, where the cover period is typed. */
    onGoToPolicy: () => void;
}

export default function InsuranceAgreementArea({
    policyId,
    agreements,
    slots,
    hasLoan,
    instrument,
    driver,
    vehicle,
    period,
    hpReference,
    hpDriverSigned,
    onDeepLink,
    onGoToSetupArea,
    onGoToPolicy,
}: Props) {
    const isNew = policyId === 'new';
    const isRecord = instrument === 'PAYMENT_RECORD';
    const who = isRecord ? 'Driver' : 'Borrower';
    const title = INSTRUMENT_TITLES[instrument];

    const slot = slots[0] ?? null;
    const document = slot?.document ?? null;
    const paper = !document && !!slot?.legacySignedAt;
    /**
     * THE SLOT'S OWN REASON, MATCHED RATHER THAN RE-DERIVED — `AgreementsArea`'s rule for the
     * partner lock, applied to this one.
     *
     * The board's predicate LIFTS for an instrument somebody has already signed, whatever the hire
     * purchase agreement is doing: voiding and reissuing the master takes the driver's signature
     * away again, and re-locking this row would hide a signature that has already been given behind
     * a padlock. A second reading of `driverSigned` here would miss that and contradict the padlock
     * on the row underneath. With no slot at all there is no document and no legacy row, so the
     * predicate reduces to the driver's arm alone — which is what this says.
     *
     * AND THERE IS NO SLOT AT ALL ON A JUNCTION THAT FINANCED NOTHING — 54 live rows. The board
     * mints an INSURANCE_LOAN slot per LOAN, so a year with no loan has no row for the padlock to
     * be about: the lede beside it already says nothing is financed, and a lock drawn over it would
     * name a hire purchase signature as the reason an instrument nobody owes has not been generated.
     */
    const locked = slot
        ? slot.blockedReason === ADVANCE_LOCK_REASON
        : !isNew && hasLoan && !hpDriverSigned;

    const vehicleName = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
    const year =
        vehicle.year === null || vehicle.year === undefined ? null : String(vehicle.year);

    /**
     * THE FIVE-WAY LEDE — no policy yet, nothing financed, a paper original, a subject with no
     * document, a document out.
     *
     * They are five genuinely different states and not five phrasings of one: before the policy
     * exists there is nothing to generate over, a year that financed nothing never owes an
     * instrument at all, a paper original is permanent, after the loan exists the send is the first
     * move, and once the document is out the only thing left to say is who is holding it.
     */
    const lede = isNew
        ? `The ${title} is generated once the policy is saved. Nothing is sent from here until then — there is no premium for it to be about.`
        : !hasLoan
          ? // THE SECOND SENTENCE IS `insurancePanelEmptyText`'s, AND IT IS PRINTED BELOW THIS ONE.
            // Two lines telling an admin to go and record the premium, one under the other, read as
            // the page repeating itself rather than as two different facts.
            'Nothing is financed on this year, so there is no instrument to generate.'
          : paper
            ? 'This year was papered outside the app, and that record is permanent: the server refuses to raise an in-app instrument over a premium already signed on paper.'
            : !document
              ? `Sending IS what generates it: the ${isRecord ? 'driver' : 'borrower'} signs it in the Primelane driver app, and Primelane countersigns. The pre-flight below names any wording the document still cannot resolve — it does not hold the send, so read it first.`
              : `The ${isRecord ? 'driver' : 'borrower'} signs this in the Primelane driver app. Primelane countersigns, and the executed PDF is filed automatically after that.`;

    return (
        <Blocks>
            <Block id="b-parties" title="Parties">
                <p className="sd-lede sd-dim">
                    The {title} names the {who.toLowerCase()} who{' '}
                    {isRecord ? 'paid the premium' : 'takes the advance'}, Primelane, and the vehicle
                    the cover is written over. None of them is edited here — they are facts about the
                    contract and about the policy.
                </p>

                <Rows>
                    <Rec
                        label={`1 · ${who}`}
                        value={driver.fullName}
                        sub={
                            <>
                                {[driver.phone, driver.address].filter(Boolean).join(' · ') ||
                                    'phone and address missing'}
                                {' · signs in the driver app · '}
                                <LinkLike onClick={() => onGoToSetupArea('driver')}>
                                    Edit in Driver
                                </LinkLike>
                            </>
                        }
                        empty="No driver assigned"
                    />
                    {/* THE SECOND PARTY IS A CONSTANT AND IS STILL WORTH DRAWING. The instrument
                        names two and an admin proof-reading it against this block should find two; a
                        row missing because its value never varies reads as a party who is missing. */}
                    <Rec
                        label="2 · Primelane"
                        value="Primelane Ghana Ltd"
                        sub={
                            isRecord
                                ? 'received the payment, and countersigns the record'
                                : 'advanced the premium, and countersigns once the driver has signed the hire purchase agreement'
                        }
                    />
                    <Rec
                        label="Vehicle"
                        value={vehicleName || null}
                        sub={
                            <>
                                {vehicle.registration || 'registration missing'}
                                {' · '}
                                <LinkLike onClick={() => onGoToSetupArea('vehicle')}>
                                    Edit in Vehicle
                                </LinkLike>
                            </>
                        }
                        empty="No vehicle on this contract"
                    />
                    {/* THE PARTICULARS GRID'S OTHER TWO, WHICH THE OPERATIONAL SCHEDULE DOES NOT
                        PRINT. Both are render-blocking merge keys like the rest, and both are the
                        Vehicle block's to correct. */}
                    <Rec
                        label="Year of manufacture"
                        value={year}
                        sub="printed in the particulars grid"
                        empty="Not recorded"
                    />
                    <Rec
                        label="Colour"
                        value={vehicle.colour}
                        sub="printed in the particulars grid"
                        empty="Not recorded"
                    />
                    {/* THE PERIOD IS THE POLICY'S, AND THE WAY BACK IS A LINK RATHER THAN A SECOND
                        PAIR OF DATE FIELDS: two doors onto one pair of columns is two drafts of it. */}
                    <Rec
                        label="Insurance period"
                        value={period}
                        sub={
                            <>
                                {'printed on both particulars grids · '}
                                {/* THE AREA'S OWN LABEL — `Cover` is the block's title on that
                                    canvas, and an admin sent to "Cover" has to work out that the
                                    tab it is on is called Policy. */}
                                <LinkLike onClick={onGoToPolicy}>Edit in Policy</LinkLike>
                            </>
                        }
                        empty="Not set"
                    />
                    {/* THE REFERENCE IS THE SERVER'S ANSWER, NOT A LOOKUP OF OUR OWN [D-B52]. Both
                        instruments quote the hire purchase agreement's number, and a contract whose
                        agreement was signed on paper resolves the designation `P-<contract id>` —
                        permanent text, because a paper-HP contract can never be given an in-app hire
                        purchase document. */}
                    <Rec
                        label="Hire purchase reference"
                        value={hpReference}
                        sub="quoted on the instrument"
                        empty="Not resolved yet"
                    />
                </Rows>

                {!hpReference && (
                    <Hint className="mt-3">
                        This contract’s hire purchase agreement has no number yet, so the instrument
                        would print the merge field literally. Generate or record the hire purchase
                        agreement on the contract’s own Agreements area first —{' '}
                        <LinkLike onClick={() => onGoToSetupArea('agreements')}>
                            Open Agreements
                        </LinkLike>
                        .
                    </Hint>
                )}
            </Block>

            {/* NEVER `· Optional` ON THIS BLOCK (`blocks.tsx`): the word may not render for it in
                any state. While there is no loan there is no subject, and the lede says what wakes
                it rather than the eyebrow wearing a badge that reads as "skippable". */}
            <Block
                id="b-insloanagr"
                title={title}
                aside={hasLoan ? `${isRecord ? 'driver' : 'borrower'} instrument` : undefined}
            >
                {/* THE SECOND SENTENCE IS A FUNCTION OF THE INSTRUMENT [D-B45]. A Payment Record
                    advances nothing and has no borrower — naming a financed premium and the person
                    who took it, over a year the driver settled himself, explains the padlock with a
                    fact that is not true of the page it is standing on. What is true either way is
                    that the document is made under the hire purchase agreement, which is why the
                    gate applies to both. */}
                {locked && (
                    <LockLine title="Locked until the driver signs the hire purchase agreement">
                        {ADVANCE_LOCK_REASON}{' '}
                        {isRecord
                            ? 'Primelane advanced nothing towards this premium, but the record is still made under the hire purchase agreement — so the driver may sign it only once the agreement it sits under has been signed.'
                            : 'A financed premium is a second bargain with credit exposure of its own, so the borrower may sign it only once the agreement it sits under has been signed.'}{' '}
                        A paper original already on file unlocks this in exactly the same way,
                        because the signature is what matters and not the document.
                    </LockLine>
                )}

                <p className="sd-lede sd-dim">{lede}</p>

                {isNew ? (
                    <Rows>
                        <Rec label={title} value={null} empty="Generated once the policy is saved" />
                    </Rows>
                ) : slots.length > 0 ? (
                    <AgreementSlotList
                        slots={slots}
                        agreements={agreements}
                        /* THE FIELD RIDES ALONG BECAUSE THE STEP IS NOT ENOUGH HERE.
                           `MERGE_FIELD_STEPS` routes the whole `insurance.` and `insurance_loan.`
                           namespaces to one step, and this dossier is three canvases. */
                        onDeepLink={onDeepLink}
                    />
                ) : (
                    /* THREE ROUND TRIPS STAND BEHIND THIS BOARD, and an unread one is not an absent
                       instrument. `insurancePanelEmptyText` is the one author of the four sentences
                       that answer an empty list, and three of them are not about this policy. */
                    <p className="sd-hint">
                        {insurancePanelEmptyText({
                            hasLoan,
                            loading: agreements.loading,
                            failed: agreements.failed,
                        })}
                    </p>
                )}
            </Block>
        </Blocks>
    );
}
